import {
  TPivotReportConfig,
  TPivotReportPreset,
} from '@infotech-soe/merps/types/Reports';
import { tg } from '@infotech-soe/metaschema';

import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { CategoryOpenMode } from '../../../models/category/category';
import ContextService from '../../../services/context/ContextService';
import UserConfigService from '../../../services/user-config/UserConfigService';
import {
  getCategoryOpenMode,
  getCategoryState,
} from '../../selectors/category';
import { getDatasetCategoryId } from '../../selectors/dataset';
import { getPivotState } from '../../selectors/pivot';
import categoryActions from '../../slices/category';
import pivot from '../../slices/pivot';
import { apiCall, createAppListener, postDispatch } from '../listenerHelpers';

const pivotReportListener = createAppListener();

const apiProvider = new MerpsApiProvider();
const userConfig = new UserConfigService();
const contextService = new ContextService();

const getConfig = (
  config?: TPivotReportConfig | TPivotReportPreset
): TPivotReportConfig | undefined => {
  if (tg.isNothing(config)) return;
  if ('currentPreset' in config) {
    return config;
  } else if ('slice' in config && config.slice) {
    return { currentPreset: 'default', presets: { default: config } };
  }
};

pivotReportListener.startListening(
  apiCall(
    {
      request: pivot.open,
      success: pivot.openSuccess,
      failure: pivot.openFailure,
    },
    async ({ dataset, command, args }, state) => {
      const categoryId = getDatasetCategoryId(state, dataset);
      const categoryState = getCategoryState(state, categoryId);
      const datasetState = state.dataset;
      const context = await contextService.getContext(
        categoryState,
        datasetState,
        dataset
      );
      const { code, data, description, error, type, ...rest } =
        (await apiProvider.actions.executeAction(
          command,
          context.toServerContext(),
          args
        )) as any;
      if (error) {
        throw error;
      }
      const pivotConfig = await userConfig.getPivotConfig(
        categoryState.source.category,
        code
      );

      if (type === 'csv') {
        const csvFile = new Blob([data], { type: 'text/csv' });
        const filename = URL.createObjectURL(csvFile);
        return {
          code,
          categoryName: categoryState.source.category,
          config: getConfig(pivotConfig) ?? getConfig(description),
          dataSource: { type, filename, ...rest },
        };
      } else {
        return {
          code,
          categoryName: categoryState.source.category,
          config: getConfig(pivotConfig) ?? getConfig(description),
          dataSource: {
            data: Array.isArray(data) ? data : JSON.parse(data),
            ...rest,
          },
        };
      }
    }
  )
);

pivotReportListener.startListening(
  apiCall(
    {
      request: pivot.close,
      success: pivot.closeSuccess,
      failure: pivot.closeFailure,
    },
    async (_payload, state, dispatch) => {
      const { categoryName, code, config, dataset, dataSource } =
        getPivotState(state);
      if (config) {
        await userConfig.savePivotConfig(categoryName, code, config);
      }
      const categoryId = getDatasetCategoryId(state, dataset);
      const openMode = getCategoryOpenMode(state, categoryId);
      if (openMode === CategoryOpenMode.HIDDEN) {
        dispatch(categoryActions.close({ categoryId }));
      }
      if (dataSource?.type === 'csv' && dataSource.filename) {
        URL.revokeObjectURL(dataSource.filename);
      }
    }
  )
);

pivotReportListener.startListening(
  apiCall(
    {
      request: pivot.savePreset,
      success: pivot.savePresetSuccess,
      failure: pivot.savePresetFailure,
    },
    async (
      { presetName, report: { dataSource, ...newPreset }, isClosed },
      state
    ) => {
      const { categoryName, code, config } = getPivotState(state);
      const presets = { ...config?.presets };

      presets[presetName] = newPreset;
      const newConfig = {
        currentPreset: presetName,
        presets,
      };
      await userConfig.savePivotConfig(categoryName, code, newConfig);
      return { newConfig, isClosed };
    }
  )
);

pivotReportListener.startListening(
  postDispatch(pivot.savePresetSuccess, ({ isClosed }, _state, dispatch) => {
    if (isClosed) {
      dispatch(pivot.close());
    }
  })
);

export default pivotReportListener.middleware;
